import { PAGE_OPEN_SECONDS_MAX, type BrowserFamily, type OsFamily, type PhoneErrorReportBody } from "@gioia365/contracts";
import { z } from "zod";
import { apiRequest } from "../api/http";
import type { TabContext } from "../session/tab-context";

// Il resoconto di un errore del telefono del browser al server (TEL-33, scelta di Claude 24/10/2026, approvata da Lucio
// 23/10/2026): quando una chiamata si ferma per un errore del Voice SDK, oltre al messaggio di sempre, il browser manda a
// `POST /api/telephony/client-errors` codice, nome e messaggio tecnico pulito dell'errore, la fase della chiamata, il `callId`
// di Gioia365, browser e sistema (famiglia e versione principale), se era in linea e da quanto la pagina era aperta. Il
// server ne scrive una riga di log. È «best effort»: parte in sottofondo, non si aspetta, non si ritenta, e un suo errore
// (rete, 429, 500) non arriva mai alla chiamata né all'interfaccia. Al massimo `PAGE_REPORT_LIMIT` resoconti per pagina in
// `PAGE_REPORT_WINDOW_MS`, come il limite per persona del server (e tre per chiamata, `call-dial.ts`): un errore che si ripete
// non diventa una tempesta di richieste, e una pagina aperta tutto il giorno torna a mandare dopo la finestra. Niente audio,
// token, numeri o dati dei Lead.

/** Quanti resoconti al massimo manda una pagina in una finestra di `PAGE_REPORT_WINDOW_MS`. */
export const PAGE_REPORT_LIMIT = 20;
export const PAGE_REPORT_WINDOW_MS = 10 * 60_000;
const CLIENT_ERRORS_PATH = "/api/telephony/client-errors";

/** Il resoconto senza ciò che aggiunge questo modulo (browser, sistema, linea, pagina). */
export type PhoneErrorReport = Omit<PhoneErrorReportBody, "browser" | "browserMajor" | "os" | "online" | "pageOpenSeconds">;

/** Quando sono partiti i resoconti dell'ultima finestra, al più `PAGE_REPORT_LIMIT`. */
let sentAt: number[] = [];

/** Soltanto per i test: riazzera i resoconti della pagina. */
export function resetPhoneErrorReportsForTests(): void {
  sentAt = [];
}

const major = (match: RegExpMatchArray | null): number | null => {
  const value = match?.[1] === undefined ? Number.NaN : Number.parseInt(match[1], 10);
  return Number.isInteger(value) && value >= 0 && value <= 9_999 ? value : null;
};

/** La famiglia del browser e la sua versione principale dallo user agent: nient'altro dello user agent esce. */
export function browserOf(userAgent: string): { browser: BrowserFamily; browserMajor: number | null } {
  // L'ordine conta: Edge e Opera dicono anche «Chrome», Chrome su iPhone dice anche «Safari».
  const rules: Array<[BrowserFamily, RegExp]> = [
    ["edge", /\bEdg(?:e|A|iOS)?\/(\d+)/],
    ["opera", /\b(?:OPR|Opera)\/(\d+)/],
    ["firefox", /\b(?:Firefox|FxiOS)\/(\d+)/],
    ["chrome", /\b(?:Chrome|CriOS|Chromium)\/(\d+)/],
    ["safari", /\bVersion\/(\d+)[^ ]* (?:Mobile\/\S+ )?Safari\//],
  ];
  for (const [browser, pattern] of rules) {
    const match = userAgent.match(pattern);
    if (match) return { browser, browserMajor: major(match) };
  }
  return { browser: "other", browserMajor: null };
}

/** La famiglia del sistema dallo user agent. */
export function osOf(userAgent: string): OsFamily {
  if (/\b(?:iPhone|iPad|iPod)\b/.test(userAgent)) return "ios";
  if (/\bAndroid\b/.test(userAgent)) return "android";
  if (/\bCrOS\b/.test(userAgent)) return "chromeos";
  if (/\b(?:Macintosh|Mac OS X)\b/.test(userAgent)) return "macos";
  if (/\bWindows\b/.test(userAgent)) return "windows";
  if (/\bLinux\b/.test(userAgent)) return "linux";
  return "other";
}

function environment(): Pick<PhoneErrorReportBody, "browser" | "browserMajor" | "os" | "online" | "pageOpenSeconds"> {
  const userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent;
  const seconds = typeof performance === "undefined" ? 0 : Math.floor(performance.now() / 1_000);
  return {
    ...browserOf(userAgent),
    os: osOf(userAgent),
    online: typeof navigator === "undefined" ? true : navigator.onLine !== false,
    pageOpenSeconds: Math.min(Math.max(seconds, 0), PAGE_OPEN_SECONDS_MAX),
  };
}

/**
 * Manda il resoconto in sottofondo, con la scheda della chiamata (l'azienda e la persona di quando è nata). Non lancia mai e
 * non restituisce nulla da aspettare; `keepalive` lo lascia arrivare anche se intanto la pagina si ricarica («Ricarica la
 * pagina» dopo un SDK che non si carica).
 */
export function reportPhoneError(tab: TabContext, report: PhoneErrorReport): void {
  try {
    const now = Date.now();
    sentAt = sentAt.filter((at) => now - at < PAGE_REPORT_WINDOW_MS);
    if (sentAt.length >= PAGE_REPORT_LIMIT) return;
    sentAt.push(now);
    const body: PhoneErrorReportBody = { ...report, ...environment() };
    void apiRequest("POST", CLIENT_ERRORS_PATH, { schema: z.unknown(), body, tab, keepalive: true }).catch(() => undefined);
  } catch {
    // Il resoconto non è mai un errore di chi chiama.
  }
}
